import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '@artstorefronts/ui';
import { Loader2, CopyIcon } from 'lucide-react';
import { toast } from 'sonner';
import { remarkDashRule } from '@arthelper/shared/chat-markdown';
import { Icon } from '../../Icon';
import { markdownComponents as components } from './common';

function SparkExecutionResult({ toolCall }: { toolCall: any }) {
  const { t } = useTranslation('chat');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!toolCall.result || isSaving) return;

    setIsSaving(true);
    try {
      const content = toolCall.result.executedSpark;

      await navigator.clipboard.writeText(content);

      toast.success(t('tool.execute-spark.copied-to-clipboard'));
    } catch (error) {
      console.error('Error saving spark execution:', error);
      toast.error(t('tool.execute-spark.failed-to-save'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!toolCall.result) return null;

  const { sparkName, sparkIcon, executedSpark } = toolCall.result;

  return (
    <div className="my-6 relative">
      <div className="bg-white border border-gray-200 rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.1)] max-w-4xl mx-auto relative">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div className="flex items-center gap-2">
            {sparkIcon ? <Icon icon={sparkIcon} size={5} /> : <div className="w-2 h-2 bg-pink-500 rounded-full"></div>}
            <h3 className="font-semibold text-gray-900">{sparkName}</h3>
          </div>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            size="sm"
            variant="ghost"
            className="flex items-center gap-2 bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CopyIcon className="w-4 h-4" />}
            {isSaving && <span className="ml-1">{t('tool.execute-spark.saving')}</span>}
          </Button>
        </div>

        <div className="p-8 bg-white rounded-b-lg">
          <div className="prose prose-gray max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-p:leading-relaxed prose-li:text-gray-700">
            <Markdown components={components} remarkPlugins={[remarkGfm, remarkDashRule]}>
              {executedSpark}
            </Markdown>
          </div>
        </div>
      </div>
    </div>
  );
}

export const execute_spark = {
  type: 'acting',
  call: () => null,
  'partial-call': () => null,
  result: ({ toolCall }: any) => <SparkExecutionResult toolCall={toolCall} />,
};
